import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { Button, device, space, Avatar, Card } from '@ticketswap/solar'
import gql from 'graphql-tag'
import { useQuery } from '@apollo/react-hooks'
import Link from 'next/link'
import { useViewer } from '~/components/ViewerContext'
import PricePerTicket from '~/components/PricePerTicket'
import { getLinkProps } from '~/utils'
import { decodeGlobalId } from '~/utils/id'
import { listingsFragment } from '~/graphql/fragments/listings'

export const StyledCard = styled(Card)`
  background-color: ${props =>
    props.isOwnedByViewer && 'rgba(0, 206, 92, 0.08)'};
  opacity: ${props => props.transparent && '0.5'};

  &:hover {
    background-color: ${props =>
      props.isOwnedByViewer && 'rgba(0, 206, 92, 0.2)'};
  }
`

export const GET_AVAILABLE_LISTINGS = gql`
  query getAvailableListings($id: ID!, $first: Int, $after: String) {
    node(id: $id) {
      ... on EventType {
        id
        slug
        title
        availableListings: listings(
          first: $first
          filter: { listingStatus: AVAILABLE }
          after: $after
        ) {
          ...listings
        }
      }
    }
  }
  ${listingsFragment}
`

const ListItem = styled.li`
  margin-top: ${space[8]};

  @media ${device.tablet} {
    margin-top: ${space[16]};
  }
`

const StyledButton = styled(Button)`
  margin-top: ${space[8]};

  @media ${device.tablet} {
    margin-top: ${space[16]};
  }
`

const Description = styled.span`
  opacity: 0.6;
`

const Available = ({ eventTypeId, resultsPerPage, t }) => {
  const { viewer } = useViewer()
  const { data = {}, loading, fetchMore } = useQuery(GET_AVAILABLE_LISTINGS, {
    notifyOnNetworkStatusChange: true,
    variables: { id: eventTypeId, first: resultsPerPage },
  })
  const { availableListings } = data.node || {}

  return (
    <>
      {availableListings?.edges?.length > 0 && (
        <ul data-testid="available-tickets-list">
          {availableListings.edges.map(listing => {
            const {
              description,
              id,
              hash,
              event,
              eventType,
              numberOfTicketsInListing,
              price,
              seller,
            } = listing.node
            const isOwnedByViewer = Boolean(viewer?.id === seller.id)

            return (
              <ListItem key={id}>
                <Link
                  {...getLinkProps('LISTING_PAGE', {
                    slug: event.slug,
                    id: decodeGlobalId(id),
                    hash: hash,
                  })}
                  passHref
                >
                  <a>
                    <StyledCard
                      isOwnedByViewer={isOwnedByViewer}
                      size="large"
                      verticalAlign="top"
                      leftAdornment={<Avatar size={44} src={seller.avatar} />}
                      title={`${numberOfTicketsInListing} × ${eventType.title}`}
                      text={event.name}
                      description={
                        description && (
                          <Description>"{description}"</Description>
                        )
                      }
                      rightAdornment={
                        <PricePerTicket
                          t={t}
                          price={
                            price.totalPriceWithTransactionFee.formattedAmount
                          }
                        />
                      }
                    />
                  </a>
                </Link>
              </ListItem>
            )
          })}
        </ul>
      )}

      {availableListings?.pageInfo.hasNextPage && (
        <StyledButton
          variant="secondary"
          width="full"
          loading={loading}
          onClick={() => {
            fetchMore({
              variables: {
                after: availableListings.pageInfo.endCursor,
              },
              updateQuery: (previousResult, { fetchMoreResult }) => {
                const newEdges = fetchMoreResult.node.availableListings.edges
                const pageInfo = fetchMoreResult.node.availableListings.pageInfo

                return newEdges.length > 0
                  ? {
                      ...fetchMoreResult,
                      node: {
                        ...fetchMoreResult.node,
                        availableListings: {
                          ...fetchMoreResult.node.availableListings,
                          edges: [
                            ...previousResult.node.availableListings.edges,
                            ...fetchMoreResult.node.availableListings.edges,
                          ],
                          pageInfo,
                        },
                      },
                    }
                  : previousResult
              },
            })
          }}
        >
          {t('common:generic.showMore')}
        </StyledButton>
      )}
    </>
  )
}

Available.defaultProps = {
  resultsPerPage: 50,
}

Available.propTypes = {
  eventTypeId: PropTypes.string,
  resultsPerPage: PropTypes.number,
  t: PropTypes.func.isRequired,
}

export default Available
